/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { useDoubleTap } from '#hooks/UNSAFE_useDoubleTap';
import { cancelEvent } from '#utils/UNSAFE_visUtils';
import { isMobile } from '#utils/UNSAFE_visUtils/clientUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

/**
 * Returns the event handlers for drill gestures.
 */
export function useVisDrill(
  selectionMode: 'none' | 'single' | 'multiple',
  onDrill: (event: Event) => void,
  isDisabled = false
) {
  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      if (selectionMode === 'none') {
        onDrill(event);
      }
    },
    [selectionMode, onDrill]
  );

  const onDoubleTap = useCallback(
    (event: Event) => {
      if (selectionMode !== 'none') {
        onDrill(event);
      }
    },
    [selectionMode, onDrill]
  );

  // The thresholds values are from Toolkit.
  // See DvtEventManager._onClick for double click detection timing,
  // and in DvtTouchManager, there's a comment above this._doubleTapTimer that says:
  // "We are using 600 ms because 300ms is too fast in VoiceOver mode to recognize the second tap."
  const doubleTapHandlers = useDoubleTap({ onDoubleTap, threshold: isMobile() ? 600 : 250 });

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onDrill(event);
        cancelEvent(event);
      }
    },
    [onDrill]
  );

  return isDisabled ? {} : mergeProps(doubleTapHandlers, { onPointerUp, onKeyDown });
}
